import { useRef, useState } from "react";
import { toast } from "@/lib/toast";
import { useStore } from "@/context/StoreContext";
import { ROLES } from "@/lib/constants";
import { Page, Empty, Modal } from "@/components/ui-z";

const PAGE_SIZE = 10;

// Pesquisa sem diferenciar maiúsculas nem acentos
const norm = (s) => (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
const roleLabel = (k) => ROLES.find((r) => r.key === k)?.label || k;

// Mesmas cores das etapas do Kanban para os níveis T1/T2/T3
const ROLE_BADGE = { atendente: "bg-secondary", t1_triagem: "bg-warning", t2_atendimento_interno: "bg-purple", t3_responsavel_tecnico: "bg-indigo", gestor: "bg-danger" };

const STATUS_FILTERS = {
  todos: () => true,
  ativos: (u) => u.active && !u.blocked,
  inativos: (u) => !u.active,
  pendentes: (u) => !u.has_password,
  bloqueados: (u) => u.blocked,
};

const StatusBadge = ({ u }) => (
  <>
    {u.blocked ? <span className="badge bg-danger"><i className="fas fa-lock mr-1" />Bloqueado</span>
      : u.active ? <span className="badge bg-success">Ativo</span>
        : <span className="badge bg-secondary">Inativo</span>}
    {!u.has_password && <span className="badge bg-warning ml-1" title="Ainda não definiu a senha: gere o link de senha">Senha pendente</span>}
  </>
);

// Gera o link temporário e mostra para o gestor copiar e enviar ao usuário
const PasswordLinkModal = ({ u, onClose }) => {
  const { createPasswordLink, sendPasswordLinkEmail } = useStore();
  const [link, setLink] = useState(null);
  const [sending, setSending] = useState(false);
  // Envia um link novo por e-mail; o link mostrado aqui (se houver) deixa de valer
  const sendEmail = async () => {
    setSending(true);
    const res = await sendPasswordLinkEmail(u.id);
    setSending(false);
    if (!res) return;
    setLink(null);
    toast.success(`Link enviado por e-mail para ${u.email}`);
  };
  const input = useRef(null);
  const generate = async () => {
    const res = await createPasswordLink(u.id);
    if (res) setLink({ url: `${window.location.origin}/definir-senha#${res.token}`, expires: new Date(res.expires_at) });
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(link.url); } catch { input.current?.select(); document.execCommand("copy"); }
    toast.success("Link copiado");
  };
  return (
    <Modal
      testId="password-link-modal" onClose={onClose}
      title={<><i className="fas fa-key mr-2" />Link de senha &middot; {u.name}</>}
      footer={<><button type="button" className="btn btn-default" data-dismiss="modal">Fechar</button>
        <div>
          <button type="button" data-testid="password-link-email" className="btn btn-outline-primary mr-2" disabled={sending} onClick={sendEmail}>
            {sending ? <i className="fas fa-spinner fa-spin mr-1" /> : <i className="fas fa-envelope mr-1" />}Enviar por e-mail
          </button>
          <button type="button" data-testid="password-link-generate" className="btn btn-primary" onClick={generate}><i className="fas fa-sync-alt mr-1" />{link ? "Gerar outro link" : "Gerar link"}</button>
        </div></>}
    >
      <p>
        Envie o link para <b>{u.email}</b> por e-mail ou gere e copie para mandar por outro canal. Ao abrir, o usuário define a
        própria senha e passa a entrar com o e-mail e essa senha.
        {u.has_password && " A senha atual dele será substituída."}
      </p>
      {link ? (
        <>
          <div className="input-group">
            <input ref={input} data-testid="password-link-url" className="form-control" value={link.url} readOnly onFocus={(e) => e.target.select()} />
            <div className="input-group-append">
              <button type="button" data-testid="password-link-copy" className="btn btn-outline-primary" onClick={copy}><i className="fas fa-copy mr-1" />Copiar</button>
            </div>
          </div>
          <small className="form-text text-muted">
            Válido até {link.expires.toLocaleString("pt-BR")} e só pode ser usado uma vez. Gerar outro link invalida este.
          </small>
        </>
      ) : (
        <p className="text-muted mb-0">Gerar um novo link invalida qualquer link anterior deste usuário.</p>
      )}
    </Modal>
  );
};

const AVATAR_COLORS = ["#007bff", "#6f42c1", "#17a2b8", "#28a745", "#fd7e14", "#e83e8c", "#20c997", "#6610f2"];
const initials = (name) => (name || "?").trim().split(/\s+/).filter(Boolean).map((w) => w[0]).filter((_, i, a) => i === 0 || i === a.length - 1).join("").toUpperCase();
const Avatar = ({ name }) => {
  const color = AVATAR_COLORS[[...(name || "")].reduce((h, c) => h + c.charCodeAt(0), 0) % AVATAR_COLORS.length];
  return (
    <span className="img-circle d-inline-flex align-items-center justify-content-center text-white font-weight-bold mr-2 flex-shrink-0"
      style={{ width: 32, height: 32, fontSize: 13, background: color }} aria-hidden="true">{initials(name)}</span>
  );
};

const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR", { day: "numeric", month: "short", year: "numeric" }) : "—");

// Criação (u = null) e edição. Bloqueado (blocked) impede o login; Ativo controla a disponibilidade para chamados
const UserModal = ({ u, onClose, onCreated }) => {
  const { createUser, updateUser } = useStore();
  const modal = useRef(null);
  const [f, setF] = useState(u ? { name: u.name, email: u.email, role: u.role, active: !!u.active, blocked: !!u.blocked } : { name: "", email: "", role: "atendente" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async (e) => {
    e?.preventDefault();
    if (!f.name.trim() || !f.email.trim()) return toast.error("Informe nome e e-mail");
    setSaving(true);
    const data = { ...f, name: f.name.trim(), email: f.email.trim() };
    const ok = u ? await updateUser(u.id, data) : await createUser(data);
    setSaving(false);
    if (!ok) return;
    if (u) {
      toast.success("Usuário atualizado");
      modal.current.hide();
      return;
    }
    // Usuário novo recebe por e-mail o convite para definir a senha; se o e-mail não saiu, segue para o link manual
    if (ok.convite?.enviado) {
      toast.success(`Usuário criado. Enviamos para ${ok.user.email} o link para definir a senha.`);
      modal.current.hide();
    } else {
      toast.warning(`Usuário criado, mas o e-mail não foi enviado. ${ok.convite?.erro ?? ""}`.trim());
      modal.current.hide(() => onCreated?.(ok.user));
    }
  };
  return (
    <Modal
      ref={modal} testId={u ? "edit-user-modal" : "new-user-modal"} onClose={onClose}
      title={u ? <><i className="fas fa-user-edit mr-2" />Editar usuário</> : <><i className="fas fa-user-plus mr-2" />Novo usuário</>}
      footer={<><button type="button" className="btn btn-default" data-dismiss="modal">Cancelar</button><button type="button" data-testid={u ? "edit-user-save-btn" : "user-submit-btn"} className="btn btn-primary" disabled={saving} onClick={save}>{saving && <i className="fas fa-spinner fa-spin mr-1" />}{u ? "Salvar" : "Cadastrar"}</button></>}
    >
      <form onSubmit={save} data-testid={u ? undefined : "new-user-form"}>
        <div className="form-group">
          <label htmlFor="user-name">Nome</label>
          <input id="user-name" data-testid={u ? "edit-user-name-input" : "user-name-input"} className="form-control" value={f.name} onChange={set("name")} />
        </div>
        <div className="form-group">
          <label htmlFor="user-email">E-mail</label>
          <input id="user-email" data-testid={u ? "edit-user-email-input" : "user-email-input"} type="email" className="form-control" value={f.email} onChange={set("email")} />
        </div>
        <div className={`form-group ${u ? "" : "mb-0"}`}>
          <label htmlFor="user-role">Nível</label>
          <select id="user-role" data-testid={u ? "edit-user-role-select" : "user-role-select"} className="form-control" value={f.role} onChange={set("role")}>
            {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </div>
        {u && (
          <>
            <div className="form-group">
              <div className="custom-control custom-switch">
                <input id="edit-user-active" data-testid="edit-user-active" type="checkbox" className="custom-control-input" checked={f.active} onChange={set("active")} />
                <label className="custom-control-label" htmlFor="edit-user-active">Ativo</label>
              </div>
              <small className="form-text text-muted">Usuário inativo não fica disponível para receber chamados.</small>
            </div>
            <div className="form-group mb-0">
              <div className="custom-control custom-switch custom-switch-on-danger">
                <input id="edit-user-blocked" data-testid="edit-user-blocked" type="checkbox" className="custom-control-input" checked={f.blocked} onChange={set("blocked")} />
                <label className="custom-control-label" htmlFor="edit-user-blocked">Bloquear usuário</label>
              </div>
              <small className="form-text text-muted">Usuário bloqueado não consegue fazer login.</small>
            </div>
          </>
        )}
        <button type="submit" className="d-none" />
      </form>
    </Modal>
  );
};

export default function Usuarios() {
  const { users, deleteUser } = useStore();
  // undefined = fechado, null = novo usuário, objeto = edição
  const [modalUser, setModalUser] = useState(undefined);
  const [linkUser, setLinkUser] = useState(null);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("todos");
  const [page, setPage] = useState(1);

  const term = norm(q.trim());
  const shown = users.filter((u) => STATUS_FILTERS[status](u) && (!role || u.role === role) &&
    (!term || [u.name, u.email, roleLabel(u.role)].some((v) => norm(v).includes(term))));
  const pages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const cur = Math.min(page, pages);
  const pageRows = shown.slice((cur - 1) * PAGE_SIZE, cur * PAGE_SIZE);
  const filter = (setter) => (e) => { setter(e.target.value); setPage(1); };

  return (
    <Page title="Usuários & Níveis" crumbs={[["Gestão"], ["Usuários"]]}>
      <div className="card">
        <div className="card-header d-flex flex-wrap align-items-center">
          <h3 className="card-title mr-auto my-1">Diretório de usuários</h3>
          <div className="d-flex flex-wrap align-items-center" style={{ gap: ".5rem" }}>
            <div className="input-group input-group-sm" style={{ width: 220 }}>
              <div className="input-group-prepend"><span className="input-group-text"><i className="fas fa-search" /></span></div>
              <input type="search" data-testid="users-search-input" className="form-control" placeholder="Pesquisar usuários" value={q} onChange={filter(setQ)} aria-label="Pesquisar usuários" />
            </div>
            <select data-testid="users-role-filter" className="form-control form-control-sm" style={{ width: 190 }} value={role} onChange={filter(setRole)} aria-label="Filtrar por nível">
              <option value="">Todos os níveis</option>
              {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
            <select data-testid="users-status-filter" className="form-control form-control-sm" style={{ width: 150 }} value={status} onChange={filter(setStatus)} aria-label="Filtrar por situação">
              <option value="todos">Todas situações</option>
              <option value="ativos">Ativos</option>
              <option value="inativos">Inativos</option>
              <option value="bloqueados">Bloqueados</option>
              <option value="pendentes">Senha pendente</option>
            </select>
            <button type="button" data-testid="new-user-btn" className="btn btn-primary btn-sm" onClick={() => setModalUser(null)}><i className="fas fa-user-plus mr-1" />Novo usuário</button>
          </div>
        </div>
        <div className="card-body table-responsive p-0">
          {users.length === 0 ? <Empty testId="users-empty" text="Nenhum usuário cadastrado." /> : shown.length === 0 ? <Empty testId="users-no-match" text="Nenhum usuário encontrado para o filtro." /> : (
            <table className="table table-hover text-nowrap mb-0" data-testid="users-table">
              <thead><tr><th>Usuário</th><th>E-mail</th><th>Nível</th><th>Situação</th><th>Criado</th><th className="text-right">Ações</th></tr></thead>
              <tbody>
                {pageRows.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.email}`}>
                    <td className="align-middle"><div className="d-flex align-items-center"><Avatar name={u.name} />{u.name}</div></td>
                    <td className="align-middle">{u.email}</td>
                    <td className="align-middle"><span className={`badge ${ROLE_BADGE[u.role] || "bg-secondary"}`} data-testid={`user-role-${u.id}`}>{roleLabel(u.role)}</span></td>
                    <td className="align-middle" data-testid={`user-status-${u.id}`}><StatusBadge u={u} /></td>
                    <td className="align-middle">{fmtDay(u.created_at)}</td>
                    <td className="align-middle text-right">
                      <div className="btn-group btn-group-sm">
                        <button type="button" data-testid={`user-password-link-${u.id}`} className={`btn ${u.has_password ? "btn-outline-secondary" : "btn-outline-warning"}`} title="Link para definir a senha" onClick={() => setLinkUser(u)}><i className="fas fa-key" /></button>
                        <button type="button" data-testid={`user-edit-${u.id}`} className="btn btn-outline-secondary" title="Editar" onClick={() => setModalUser(u)}><i className="fas fa-pen" /></button>
                        <button type="button" data-testid={`user-delete-${u.id}`} className="btn btn-outline-danger" title="Remover" onClick={async () => { if (await deleteUser(u.id)) toast("Usuário removido"); }}><i className="fas fa-trash" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {shown.length > 0 && (
          <div className="card-footer d-flex flex-wrap align-items-center">
            <small className="text-muted mr-auto" data-testid="users-count">
              Mostrando {(cur - 1) * PAGE_SIZE + 1} a {(cur - 1) * PAGE_SIZE + pageRows.length} de {shown.length} usuário(s)
            </small>
            {pages > 1 && (
              <ul className="pagination pagination-sm m-0" data-testid="users-pagination">
                <li className={`page-item ${cur === 1 ? "disabled" : ""}`}><button type="button" className="page-link" aria-label="Anterior" onClick={() => setPage(cur - 1)}>&laquo;</button></li>
                {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
                  <li key={n} className={`page-item ${n === cur ? "active" : ""}`}><button type="button" className="page-link" onClick={() => setPage(n)}>{n}</button></li>
                ))}
                <li className={`page-item ${cur === pages ? "disabled" : ""}`}><button type="button" className="page-link" aria-label="Próxima" onClick={() => setPage(cur + 1)}>&raquo;</button></li>
              </ul>
            )}
          </div>
        )}
      </div>
      {modalUser !== undefined && <UserModal u={modalUser} onClose={() => setModalUser(undefined)} onCreated={setLinkUser} />}
      {linkUser && <PasswordLinkModal u={linkUser} onClose={() => setLinkUser(null)} />}
    </Page>
  );
}
